// Pure helpers for the admin review queue: what to highlight in a submitted
// manifest, a line-highlighted pretty JSON, queue ordering and reject notes.
import { PERMISSION_IDS, REVIEW_RULES } from '~/lib/plugin-sdk';
import type { VersionView } from '@dunea/shared';

/** Top-level manifest keys a reviewer must look at closely. */
export const REVIEW_KEYS = ['permissions', 'siteScripts', 'webhookUrl', 'panel', 'agentTools', 'pricing'] as const;

export interface ManifestHighlights {
  permissions: string[];
  /** Permissions this version adds over the previous approved one (read from the automated checks). */
  newPermissions: string[];
  /** The check message that reported them, verbatim. */
  newPermissionsNote: string | null;
  siteScripts: { src: string; consent: string; placement: string }[];
  webhookUrl: string | null;
  panelUrl: string | null;
  agentTools: { name: string; description: string }[];
}

const str = (v: unknown): string | null => (typeof v === 'string' && v ? v : null);

/**
 * The automated checks are free-form ({ name, ok, message }); the one about new
 * permissions is found by name (/permission/) and the permission ids it
 * mentions are pulled out of its message.
 */
export function manifestHighlights(
  manifest: Record<string, unknown> | null | undefined,
  checks: VersionView['checks'] = [],
): ManifestHighlights {
  const m = manifest ?? {};
  const permissions = Array.isArray(m.permissions)
    ? m.permissions.filter((p): p is string => typeof p === 'string')
    : [];
  const check = checks.find((c) => /new.?permission|permission.?(diff|change|added)/i.test(c.name)) ?? null;
  const mentioned = check ? PERMISSION_IDS.filter((id) => check.message.includes(id)) : [];
  const panel = m.panel && typeof m.panel === 'object' ? (m.panel as Record<string, unknown>) : null;

  return {
    permissions,
    newPermissions: mentioned,
    newPermissionsNote: check?.message ?? null,
    siteScripts: Array.isArray(m.siteScripts)
      ? m.siteScripts
          .filter((s): s is Record<string, unknown> => Boolean(s) && typeof s === 'object')
          .map((s) => ({ src: str(s.src) ?? '', consent: str(s.consent) ?? '', placement: str(s.placement) ?? 'body' }))
      : [],
    webhookUrl: str(m.webhookUrl),
    panelUrl: panel ? str(panel.url) : null,
    agentTools: Array.isArray(m.agentTools)
      ? m.agentTools
          .filter((t): t is Record<string, unknown> => Boolean(t) && typeof t === 'object')
          .map((t) => ({ name: str(t.name) ?? '', description: str(t.description) ?? '' }))
      : [],
  };
}

export interface JsonLine {
  text: string;
  /** Inside one of the REVIEW_KEYS top-level blocks. */
  highlight: boolean;
}

/** Pretty JSON, line by line, flagging the top-level blocks a reviewer must read. */
export function manifestJsonLines(
  manifest: Record<string, unknown> | null | undefined,
  keys: readonly string[] = REVIEW_KEYS,
): JsonLine[] {
  const text = JSON.stringify(manifest ?? {}, null, 2);
  const wanted = new Set(keys);
  let current: string | null = null;

  return text.split('\n').map((line) => {
    const top = /^ {2}"([^"]+)":/.exec(line);

    if (top) {
      current = top[1];
    } else if (/^[{}]/.test(line)) {
      current = null;
    }

    return { text: line, highlight: current !== null && wanted.has(current) };
  });
}

/** Oldest submission first (the API already orders it; this keeps it stable client-side). */
export function sortQueue(queue: readonly VersionView[]): VersionView[] {
  const time = (v: VersionView) => Date.parse(v.submittedAt ?? '') || 0;
  return [...queue].sort((a, b) => time(a) - time(b));
}

/** "3d", "5h", "12m" since submission. */
export function waitingFor(submittedAt: string | null, now = Date.now()): string {
  const t = Date.parse(submittedAt ?? '');

  if (!t) {
    return '';
  }

  const minutes = Math.max(0, Math.floor((now - t) / 60_000));

  if (minutes < 60) {
    return `${minutes}m`;
  }

  const hours = Math.floor(minutes / 60);

  return hours < 48 ? `${hours}h` : `${Math.floor(hours / 24)}d`;
}

export function rejectNotesError(notes: string): string | null {
  return notes.trim().length < 10 ? 'Tell the developer what to fix (at least 10 characters).' : null;
}

/** The checklist, grouped by rule family (M manifest, U urls, L listing…), in id order. */
export const RULE_GROUPS: { label: string; rules: { id: string; text: string }[] }[] = (() => {
  const names: Record<string, string> = {
    M: 'Manifest',
    U: 'Endpoints',
    L: 'Listing',
    P: 'Permissions',
    B: 'Billing',
    S: 'Site scripts',
    T: 'Themes and templates',
    K: 'Skills',
    Q: 'Quality',
  };
  const groups = new Map<string, { id: string; text: string }[]>();

  for (const [id, text] of Object.entries(REVIEW_RULES)) {
    const family = /^DUN-([A-Z])/.exec(id)?.[1] ?? '?';
    groups.set(family, [...(groups.get(family) ?? []), { id, text }]);
  }

  return Array.from(groups, ([family, rules]) => ({ label: names[family] ?? family, rules }));
})();

/** A rejection must cite at least one known rule. */
export function rejectionError(codes: readonly string[]): string | null {
  const known = codes.filter((c) => c in REVIEW_RULES);
  return known.length === 0 ? 'Pick at least one rule the version breaks.' : null;
}

/** Notes sent with a rejection: what the reviewer typed, else the cited rules' text. */
export function rejectionNotes(codes: readonly string[], notes: string): string {
  const typed = notes.trim();

  if (typed) {
    return typed;
  }

  return codes
    .filter((c) => c in REVIEW_RULES)
    .map((c) => `${c}: ${(REVIEW_RULES as Record<string, string>)[c]}`)
    .join('\n');
}

export function toggleCode(codes: readonly string[], id: string): string[] {
  return codes.includes(id) ? codes.filter((c) => c !== id) : [...codes, id];
}

export function failingChecks(checks: VersionView['checks']): number {
  return checks.filter((c) => !c.ok).length;
}
